import { useListener, useSubmit } from "@hyper-fetch/react";
import { useCallback, useEffect, useState } from "react";

import { client } from "@/api/client";
import { sdk, socketSdk } from "@/api/sdk";

const notificationsCachePattern = /\/notifications/;

/**
 * Owns the bell badge count. On mount (and whenever a new notification arrives)
 * it calls the POST `sync` endpoint, which materializes any pending broadcast
 * campaigns for this user and returns the fresh unread count. Materialization is
 * a write, so it lives behind POST; the list endpoint stays a pure read and
 * refetches via cache invalidation once sync lands.
 */
export const useUnreadCount = () => {
  const [count, setCount] = useState(0);

  const { submit, submitting, onSubmitSuccess } = useSubmit(sdk.notifications.sync.$post);

  const countListener = useListener(socketSdk.notification.count.$listener);
  const newListener = useListener(socketSdk.notification.new.$listener);

  const sync = useCallback(() => {
    submit();
  }, [submit]);

  onSubmitSuccess(({ response }) => {
    setCount(Number(response.data.unreadCount) || 0);
    client.cache.invalidate(notificationsCachePattern);
  });

  useEffect(() => {
    sync();
  }, []);

  countListener.onEvent(({ data: event }) => {
    setCount(Number(event.unreadCount) || 0);
  });

  newListener.onEvent(() => {
    sync();
  });

  const reset = useCallback(() => {
    setCount(0);
  }, []);

  return { count, loading: submitting, refetch: sync, reset };
};
